import { Injectable, NgModule } from '@angular/core';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { combineLatest, defer, Observable, of, throwError } from 'rxjs';
import { catchError, filter, finalize, shareReplay, switchMap, take, tap, timeout } from 'rxjs/operators';

import { ArmyBuilderConfig } from './config';
import { NetworkActivityService } from './network-activity.service';
import { SettingsService } from './settings/service/settings.service';

export interface HttpClientRequestOptions {
    requiresLogin?: boolean;
    withCredentials?: boolean;
    headers?: any;
    timeoutMs?: number;
    [key: string]: any;
}

const inFlightRequests: { [key: string]: Observable<any> } = {};

@Injectable()
export class HttpClientWithInFlightCache {
    constructor(
        private config: ArmyBuilderConfig,
        private httpClient: HttpClient,
        private settingsService: SettingsService,
        private networkActivity: NetworkActivityService
    ) {}

    private request(method: 'get' | 'post' | 'put' | 'patch' | 'delete', url: string, body: any, opts: HttpClientRequestOptions) {
        if (method === 'get' && inFlightRequests[url]) {
            // console.log('Reusing existing request for ' + url);
            return inFlightRequests[url];
        }

        const req = combineLatest([this.config.device$, this.settingsService.login$]).pipe(
            filter(([device, l]) => {
                return !!device?.id && (!opts?.requiresLogin || l.user);
            }),
            take(1),
            switchMap(([device, l]) => {
                opts = {
                    ...opts,
                    headers: {
                        ...opts?.headers,
                        device: JSON.stringify(device),
                        appVersion: this.config.version
                    }
                };
                if (opts.withCredentials !== false) {
                    if (l.user?.accessToken) {
                        opts.headers.Authorization = 'Bearer ' + l.user?.accessToken;
                    }
                }
                switch (method) {
                    case 'get':
                        return this.httpClient.get(url, {
                            ...opts,
                            withCredentials: opts?.withCredentials ?? true
                        });
                    case 'delete':
                        return this.httpClient.delete(url, {
                            ...opts,
                            body,
                            withCredentials: opts?.withCredentials ?? true
                        });
                    default:
                        return this.httpClient[method](url, body, {
                            ...opts,
                            withCredentials: opts?.withCredentials ?? true
                        });
                }
            }),
            timeout(opts?.timeoutMs ?? this.config.networkTimeout),
            shareReplay(1)
        );
        // Counted on subscribe rather than on creation, so a request nobody
        // subscribes to never leaves the ribbon stuck on. defer re-runs per
        // subscriber and finalize fires on complete, error and unsubscribe
        // alike, so the count is always balanced.
        const tracked = defer(() => {
            this.networkActivity.started();
            return req.pipe(finalize(() => this.networkActivity.finished()));
        });

        if (method === 'get') {
            inFlightRequests[url] = tracked;
            tracked.pipe(catchError(() => of(null))).subscribe(() => {
                delete inFlightRequests[url];
            });
        }
        return tracked;
    }

    get<T = any>(url: string, opts?: HttpClientRequestOptions): Observable<T> {
        return this.request('get', url, null, opts);
    }

    post<T = any>(url: string, body: any, opts?: HttpClientRequestOptions): Observable<T> {
        return this.request('post', url, body, opts);
    }

    put<T = any>(url: string, body: any, opts?: HttpClientRequestOptions): Observable<T> {
        return this.request('put', url, body, opts);
    }

    patch<T = any>(url: string, body: any, opts?: HttpClientRequestOptions): Observable<T> {
        return this.request('patch', url, body, opts);
    }

    delete<T = any>(url: string, body?: any, opts?: HttpClientRequestOptions): Observable<T> {
        return this.request('delete', url, body, opts);
    }
}

@NgModule({
    exports: [],
    declarations: [],
    imports: [],
    providers: [HttpClientWithInFlightCache, provideHttpClient(withInterceptorsFromDi())]
})
export class HttpClientWithInFlightCacheModule {}
